import React from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import type { BaseTextInputProperties } from './BaseTextInput.types';

/**
 * Базовый текстовый инпут на основе composed MUI компонентов.
 * Label рендерится через InputLabel с shrink — всегда над полем.
 * OutlinedInput с notched={false} — чистый border без выреза.
 *
 * `slotProps.input` и `slotProps.htmlInput` следуют MUI v9 контракту —
 * это позволяет напрямую раскрывать `AutocompleteRenderInputParams`
 * через `<BaseTextInput {...params} />` в Autocomplete.renderInput.
 */
export const BaseTextInput = React.forwardRef<
  HTMLDivElement,
  BaseTextInputProperties
>(
  (
    {
      dataTest,
      label,
      id,
      error,
      disabled,
      placeholder,
      helperText,
      sx = { width: '100%' },
      required,
      size,
      slotProps,
      onAutoFill,
      inputRef,
      ...outlinedInputProperties
    }: BaseTextInputProperties,
    reference,
  ) => {
    React.useEffect(() => {
      if (!inputRef || typeof inputRef !== 'object' || !inputRef.current) {
        return;
      }
      const inputElement = inputRef.current;
      const handleInput = () => {
        if (onAutoFill && inputElement.value) {
          onAutoFill(inputElement.value);
        }
      };
      inputElement.addEventListener('input', handleInput);
      return () => {
        inputElement.removeEventListener('input', handleInput);
      };
    }, [inputRef, onAutoFill]);

    const {
      ref: slotInputReference,
      sx: slotInputSx,
      className: slotInputClassName,
      startAdornment: slotStartAdornment,
      endAdornment: slotEndAdornment,
      onMouseDown: slotInputMouseDown,
    } = slotProps?.input ?? {};

    return (
      <FormControl
        fullWidth
        error={error}
        disabled={disabled}
        required={required}
        sx={sx}
        ref={reference}
        data-testid={dataTest}
      >
        {label && (
          <InputLabel
            htmlFor={id}
            shrink
            data-testid={dataTest ? `${dataTest}_label` : undefined}
            sx={{
              position: 'relative',
              transform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              mb: 0.75,
              '&.Mui-focused': {
                color: error ? 'error.main' : 'text.secondary',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <OutlinedInput
          id={id}
          placeholder={
            placeholder ?? (typeof label === 'string' ? label : undefined)
          }
          notched={false}
          label={undefined}
          size={size}
          inputRef={inputRef}
          ref={slotInputReference}
          sx={slotInputSx}
          className={slotInputClassName}
          startAdornment={slotStartAdornment}
          endAdornment={slotEndAdornment}
          onMouseDown={slotInputMouseDown}
          inputProps={{
            'data-testid': dataTest ? `${dataTest}_input` : undefined,
            ...slotProps?.htmlInput,
          }}
          {...outlinedInputProperties}
        />
        {helperText && (
          <FormHelperText data-testid={dataTest ? `${dataTest}_helper` : undefined}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);

BaseTextInput.displayName = 'BaseTextInput';
